import { useEffect, useMemo, useState } from 'react';
import { FactCheck, CloudDownload, Edit, Preview } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { LoadingButton } from '@mui/lab';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import { Link, useHistory } from 'react-router-dom';
// Components
import { Modal, OrderManagementPage, PageTitleWithButton, Table } from '../../../components';
import { InvalidPosModal } from './invalid-pos-modal';
import { PendingChecklistFilters } from './pending-checklist-filters';
// Helpers, Fecth, Models
import {
  checklistsParentLink,
  downloadCSV,
  formatMoney,
  formatCapitalCase
} from '../../../helpers';
import {
  IDropdownResponse,
  IOrderType,
  IPlaceOrderChecklist,
  IPlaceOrderChecklistJob,
  IPlaceOrderChecklistTerritory,
  IVendor
} from '../../../models';
import {
  getPlaceOrderChecklists,
  getPlaceOrderChecklistJobs,
  getPlaceOrderChecklistTerritories,
  getPlaceOrderTypes,
  getPlaceOrderChecklistStatuses
} from '../../../fetch';
import { getProductionVendors } from '../../../fetch/lookups';
import { ChecklistDetails } from './ChecklistDetails';

export const ManageChecklists = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [queryString, setQueryString] = useState('');

  const isMobile = useMediaQuery('(max-width: 1024px)');

  const [isDownloadingCSV, setIsDownloadingCSV] = useState<boolean>(false);
  const [isShowingInvalidModal, setIsShowingInvalidModal] = useState(false);
  const [selectedTerritory, setSelectedTerritory] = useState<IPlaceOrderChecklistTerritory | null>(
    null
  );
  const [selectedJob, setSelectedJob] = useState<IPlaceOrderChecklistJob | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<IVendor | null>(null);
  const [selectedType, setSelectedType] = useState<IOrderType | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<IDropdownResponse | null>(null);
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [purchaseOrderId, setPurchaseOrder] = useState<number | null>(null);
  const [isTerritoriesLoaded, setIsTerritoriesLoaded] = useState(false);
  const [isLoadingTerritories, setTerritoriesLoading] = useState(false);
  const [territories, setTerritories] = useState<IPlaceOrderChecklistTerritory[]>([]);
  const fetchTerritories = async () => {
    setTerritoriesLoading(true);
    try {
      const res = await getPlaceOrderChecklistTerritories();
      setTerritories(res);
      setIsTerritoriesLoaded(true);
    } catch (error) {
      console.log(error);
    } finally {
      setTerritoriesLoading(false);
    }
  };

  const [isJobsLoaded, setIsJobsLoaded] = useState(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobs, setJobs] = useState<IPlaceOrderChecklistJob[]>([]);
  const fetchJobs = async () => {
    try {
      setIsLoadingJobs(true);
      const res = await getPlaceOrderChecklistJobs(selectedTerritory.territoryId);
      setJobs(res);
      setIsJobsLoaded(true);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading jobs, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const [isLoadingVendors, setLoadingVendors] = useState(false);
  const [vendors, setVendors] = useState<IVendor[] | null>(null);
  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const res = await getProductionVendors();
      setVendors(res.records);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading vendors, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingVendors(false);
    }
  };

  const [isLoadingTypes, setLoadingTypes] = useState(false);
  const [types, setTypes] = useState<IOrderType[] | null>(null);
  const fetchTypes = async () => {
    try {
      setLoadingTypes(true);
      const res = await getPlaceOrderTypes();
      setTypes(res);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading types, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingTypes(false);
    }
  };

  const [isLoadingStatuses, setLoadingStatuses] = useState(false);
  const [statuses, setStatuses] = useState<IDropdownResponse[] | null>(null);
  const fetchStatuses = async () => {
    try {
      setLoadingStatuses(true);
      const res = await getPlaceOrderChecklistStatuses();
      setStatuses(res);
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error loading statuses, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingStatuses(false);
    }
  };

  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filters, setFilters] = useState<{
    orderNumber?: number | string;
    territoryId?: string | number | null;
    jobSageId?: string | number | null;
    sageVendorId?: string | number | null;
    sageOrderTypeId?: string | number | null;
    status?: string | null;
  }>({});

  const [searchValue, setSearch] = useState<string>('');

  const [selectedSort, setSelectedSort] = useState<string>('OrderNumber');
  const [sortDirection, setSortDirection] = useState<{
    OrderNumber?: 'Asc' | 'Desc';
    Vendor?: 'Asc' | 'Desc';
    Job?: 'Asc' | 'Desc';
    Task?: 'Asc' | 'Desc';
    Type?: 'Asc' | 'Desc';
    Status?: 'Asc' | 'Desc';
    Dollars?: 'Asc' | 'Desc';
  }>({ OrderNumber: 'Asc' });

  const [isLoadingChecklists, setLoadingChecklists] = useState(false);
  const [checklists, setChecklists] = useState<IPlaceOrderChecklist[]>([]);

  const fetchChecklists = async () => {
    try {
      setLoadingChecklists(true);
      const res = await getPlaceOrderChecklists({
        orderNumber: searchValue,
        territoryId: selectedTerritory?.territoryId,
        jobSageId: selectedJob?.jobSageId,
        sageVendorId: selectedVendor?.sageId,
        sageOrderTypeId: selectedType?.sageId,
        status: selectedStatus?.value,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      setChecklists(res);
    } catch (error) {
      enqueueSnackbar(`Error loading checklists, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingChecklists(false);
    }
  };

  const updateUrlWithFilters = newFilters => {
    const queryParams = new URLSearchParams();
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        queryParams.set(key, newFilters[key]);
      }
    });
    const newQueryString = queryParams.toString();
    setQueryString(newQueryString);
    history.push({ search: newQueryString });
  };

  useEffect(() => {
    fetchTerritories();
    fetchVendors();
    fetchTypes();
    fetchStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isTerritoriesLoaded) {
      return;
    }
    const queryParams = new URLSearchParams(window.location.search);
    const urlFilters: any = {};
    queryParams.forEach((value, key) => {
      urlFilters[key] = value;
    });

    if (Object.keys(urlFilters).length !== 0) {
      if (urlFilters.territoryId) {
        const foundTerritory = territories?.find(
          t => t.territoryId === Number(urlFilters.territoryId)
        );
        setSelectedTerritory(foundTerritory);
      }
      if (urlFilters?.jobSageId) {
        const foundJob = jobs?.find(job => job.jobSageId === Number(urlFilters.jobSageId));
        setSelectedJob(foundJob);
      }
      if (urlFilters?.orderNumber) {
        setSearch(urlFilters.orderNumber);
      }
      if (urlFilters?.sageVendorId) {
        const foundVendor = vendors?.find(
          vendor => vendor.sageId === Number(urlFilters.sageVendorId)
        );
        setSelectedVendor(foundVendor);
      }
      if (urlFilters?.sageOrderTypeId) {
        const foundOrderType = types?.find(
          type => type.sageId === Number(urlFilters.sageOrderTypeId)
        );
        setSelectedType(foundOrderType);
      }
      if (urlFilters?.status) {
        const foundStatus = statuses.find(status => status.value === urlFilters.status);
        setSelectedStatus(foundStatus);
      }

      updateUrlWithFilters(urlFilters);
      setFilters(urlFilters);
      setHasAppliedFilters(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTerritoriesLoaded, isJobsLoaded, jobs]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const urlFilters: any = {};
    queryParams.forEach((value, key) => {
      urlFilters[key] = value;
    });

    if (
      selectedTerritory?.territoryId &&
      urlFilters?.territoryId &&
      Number(urlFilters?.territoryId) !== Number(selectedTerritory?.territoryId)
    ) {
      setSearchedValue('');
      setSelectedVendor(null);
      setSelectedType(null);
      setSelectedStatus(null);
      setHasAppliedFilters(false);
      updateUrlWithFilters({});
      fetchJobs();
    } else if (selectedTerritory?.territoryId) {
      fetchJobs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerritory]);

  useEffect(() => {
    if (selectedTerritory?.territoryId && selectedJob?.jobSageId) {
      fetchChecklists();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortDirection, selectedSort]);

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const isColumnSortable = (column: string) => {
    return selectedSort === column;
  };

  const isColumnSortDesc = (column: any) => {
    return column === 'Desc';
  };

  // MOBILE TABLE //
  const mobileColumns = useMemo(() => {
    return [
      {
        Header: 'Vendor',
        accessor: 'vendorName',
        sort: true,
        isServerSorted: isColumnSortable('Vendor'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Vendor),
        handleClickColumn: () => handleClickColumn('Vendor'),
        headerClassName: isMobile && classes.mobileTitle,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPlaceOrderChecklist } };
        }) => {
          return (
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" className={classes.ellipsis}>
                {formatCapitalCase(original.vendorName)} {' - '} {formatCapitalCase(original.task)}
              </Typography>
              <Button
                id="edit"
                color="primary"
                sx={{ p: 0, display: 'flex', justifyContent: 'flex-end', minWidth: '1.5rem' }}
                className={classes.customIconButton}
                startIcon={<Edit />}
                component={Link}
                to={`/production/checklists/manage-checklists/${original.purchaseOrderId}?${queryString}`}
              />
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, queryString]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Order Number',
        accessor: 'orderNumber',
        sort: true,
        isServerSorted: isColumnSortable('OrderNumber'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.OrderNumber),
        handleClickColumn: () => handleClickColumn('OrderNumber')
      },
      {
        Header: 'Vendor',
        accessor: 'vendorName',
        sort: true,
        isServerSorted: isColumnSortable('Vendor'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Vendor),
        handleClickColumn: () => handleClickColumn('Vendor')
      },

      {
        Header: 'Job',
        accessor: 'job',
        sort: true,
        isServerSorted: isColumnSortable('Job'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Job),
        handleClickColumn: () => handleClickColumn('Job')
      },
      {
        Header: 'Task',
        accessor: 'task',
        sort: true,
        isServerSorted: isColumnSortable('Task'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Task),
        handleClickColumn: () => handleClickColumn('Task')
      },
      {
        Header: 'Type',
        accessor: 'type',
        sort: true,
        isServerSorted: isColumnSortable('Type'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Type),
        handleClickColumn: () => handleClickColumn('Type')
      },
      {
        Header: 'Status',
        accessor: 'status',
        sort: true,
        isServerSorted: isColumnSortable('Status'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Status),
        handleClickColumn: () => handleClickColumn('Status')
      },
      {
        Header: 'Dollars',
        accessor: 'dollars',
        sort: true,
        isServerSorted: isColumnSortable('Dollars'),
        isServerSortedDesc: isColumnSortDesc(sortDirection.Dollars),
        handleClickColumn: () => handleClickColumn('Dollars'),
        Cell: ({ cell }) => {
          return <>{formatMoney(cell.row.original.dollars, 2)}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPlaceOrderChecklist } };
        }) => {
          return (
            <Box display="flex" alignItems="center">
              <Button
                id="edit"
                color="primary"
                startIcon={<FactCheck />}
                component={Link}
                to={`/production/checklists/manage-checklists/${original.purchaseOrderId}?${queryString}`}
                fullWidth
              >
                Perform Checklist
              </Button>
              <Button
                id="preview"
                color="secondary"
                startIcon={<Preview />}
                onClick={() => {
                  setPurchaseOrder(original.purchaseOrderId);
                }}
                fullWidth
              >
                Preview
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, queryString]);

  const handleCSVDownload = async () => {
    try {
      setIsDownloadingCSV(true);

      const res = await getPlaceOrderChecklists({
        orderNumber: searchValue,
        territoryId: selectedTerritory?.territoryId,
        jobSageId: selectedJob?.jobSageId,
        sageVendorId: selectedVendor?.sageId,
        sageOrderTypeId: selectedType?.sageId,
        status: selectedStatus?.value,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      });
      // Initialize CSV with headers
      let csv = `${columns.map(c => c.Header).join(', ')}\n`;

      res?.forEach(checklist => {
        if (checklist.orderNumber) {
          csv += `${checklist.orderNumber},`;
        } else {
          csv += ',';
        }

        if (checklist.vendorName) {
          csv += `"${checklist.vendorName}",`;
        } else {
          csv += ',';
        }
        if (checklist.job) {
          csv += `${checklist.job},`;
        } else {
          csv += ',';
        }
        if (checklist.task) {
          csv += `${checklist.task},`;
        } else {
          csv += ',';
        }
        if (checklist.type) {
          csv += `${checklist.type},`;
        } else {
          csv += ',';
        }
        if (checklist.status) {
          csv += `${checklist.status},`;
        } else {
          csv += ',';
        }
        if (checklist.dollars !== null) {
          csv += `"${formatMoney(checklist.dollars)}",`;
        } else {
          csv += ',';
        }
        csv += '\n';
      });
      downloadCSV(
        csv,
        `checklists_${format(parseISO(new Date().toISOString()), 'yyyy_LL_dd_hh_mm_ss')}`
      );
    } catch (error) {
      console.log(error);
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  const isTableVisible = hasAppliedFilters && isTerritoriesLoaded && isJobsLoaded;

  return (
    <Box className="print--none">
      <OrderManagementPage
        title="Manage Checklists"
        requiredFeature={checklistsParentLink.featureFlag}
        legacyUrl={checklistsParentLink.legacyUrl}
        hasNoContentPadding={isMobile && true}
      >
        <Grid container className={classes.titleWrapper}>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitleWithButton
              pageTitleProps={{
                title: 'Manage Checklists',
                showDivider: false,
                marginBottom: false
              }}
              button={
                <Button
                  onClick={() => setIsShowingInvalidModal(true)}
                  color="secondary"
                  variant="contained"
                  className={classes.addButton}
                >
                  Check for Invalid POs
                </Button>
              }
            />
          </Grid>
        </Grid>
        <PendingChecklistFilters
          isLoading={isLoadingChecklists}
          searchedValue={searchedValue}
          setSearchedValue={setSearchedValue}
          territories={territories}
          isLoadingTerritories={isLoadingTerritories}
          jobs={jobs}
          isLoadingJobs={isLoadingJobs}
          vendors={vendors}
          isLoadingVendors={isLoadingVendors}
          statuses={statuses}
          isLoadingStatuses={isLoadingStatuses}
          types={types}
          isLoadingTypes={isLoadingTypes}
          hasAppliedFilters={hasAppliedFilters}
          setHasAppliedFilters={setHasAppliedFilters}
          applyFilters={(clearFilters?: boolean) => {
            let newFilters = {};
            if (clearFilters) {
              setFilters({
                orderNumber: null,
                territoryId: null,
                jobSageId: null,
                sageVendorId: null,
                sageOrderTypeId: null,
                status: null
              });
            } else {
              newFilters = {
                ...filters,
                orderNumber: searchValue,
                territoryId: selectedTerritory?.territoryId,
                jobSageId: selectedJob?.jobSageId,
                sageVendorId: selectedVendor?.sageId,
                sageOrderTypeId: selectedType?.sageId,
                status: selectedStatus?.value
              };
            }
            setFilters(newFilters);
            updateUrlWithFilters(newFilters);
          }}
          selectedTerritory={selectedTerritory}
          setSelectedTerritory={setSelectedTerritory}
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          selectedVendor={selectedVendor}
          setSelectedVendor={setSelectedVendor}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          fetchChecklists={fetchChecklists}
          setChecklists={setChecklists}
          searchValue={searchValue}
          setSearchValue={setSearch}
        />

        {isTableVisible && (
          <>
            <Divider className={classes.divider} />
            {!isMobile && (
              <Box display="flex" justifyContent="flex-end">
                <LoadingButton
                  loading={isDownloadingCSV}
                  loadingPosition="start"
                  id={'csv_download'}
                  color="primary"
                  variant="contained"
                  disabled={isLoadingChecklists || isDownloadingCSV}
                  onClick={handleCSVDownload}
                  startIcon={<CloudDownload />}
                >
                  CSV
                </LoadingButton>
              </Box>
            )}
            <Table
              columns={isMobile ? mobileColumns : columns}
              data={checklists}
              isLoading={isLoadingChecklists}
              cellClasses={clsx(isMobile && classes.mobilePadding)}
              containerClasses={isMobile && classes.mobileTableContainer}
            />
          </>
        )}
        {(!hasAppliedFilters || !isTerritoriesLoaded || !isJobsLoaded) && (
          <>
            <Divider className={classes.divider} />
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="5rem"
            >
              <Typography>
                Please select a Territory and then a Job to view associated checklists.
              </Typography>
            </Box>
          </>
        )}
        <InvalidPosModal
          isOpen={isShowingInvalidModal}
          onClose={() => setIsShowingInvalidModal(false)}
        />
        {purchaseOrderId !== null && (
          <Modal maxWidth="lg" open onClose={() => setPurchaseOrder(null)}>
            <ChecklistDetails queryId={`${purchaseOrderId}`} isModalPreview />
          </Modal>
        )}
      </OrderManagementPage>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  addButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5)
  },
  divider: {
    margin: `${theme.spacing(1)} 0`
  },
  mobileTitle: {
    padding: '.5rem 0 .25rem .25rem'
  },
  mobilePadding: {
    padding: '1rem .25rem'
  },
  ellipsis: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '21rem', // Default max-width
    [theme.breakpoints.down('sm')]: {
      maxWidth: '21rem' // Smaller max-width for small screens
    },
    '@media (max-width:400px)': {
      maxWidth: '19rem' // max-width for screens 400px or below
    },
    '@media (max-width:360px)': {
      maxWidth: '15.5rem'
    }
  },
  mobileTableContainer: {
    overflowX: 'hidden'
  },
  customIconButton: {
    minWidth: '1.5rem'
  }
}));
